import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { ChevronDown, ChevronUp, Cpu, Building, Users, Leaf, Heart, Globe, Target, Shield, CheckCircle, Wind, Settings, Zap, Compass, Lightbulb, FileText, CloudRain, Scale, Calendar, UserCheck, Link } from 'lucide-react';

const Resources = () => {
  const [expandedSection, setExpandedSection] = useState(null);
  const [expandedWindSection, setExpandedWindSection] = useState(null);
  const [expandedP7021Section, setExpandedP7021Section] = useState(null);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const toggleWindSection = (section) => {
    setExpandedWindSection(expandedWindSection === section ? null : section);
  };

  const toggleP7021Section = (section) => {
    setExpandedP7021Section(expandedP7021Section === section ? null : section);
  };

  const p7021Sections = [
    {
      id: 'what',
      title: 'What is this standard?',
      icon: FileText,
      color: 'bg-blue-600',
      content: `IEEE P7021 is an IEEE recommended practice that provides practical guidance for implementing ethical AI in long-term climate projections and disaster risk reduction. Climate modelling is increasingly using machine learning, hybrid physics–AI methods, climate foundation models, and Digital Twins. These tools introduce governance challenges. They include deep uncertainty, long time horizons, uneven or contested data, and high-stakes impacts on communities, ecosystems, and critical infrastructure.

P7021 sets out actionable methods for fairness and bias mitigation, transparency and explainability, accountability, and meaningful human oversight. The goal is to ensure AI-augmented projections can be trusted, tested, and used responsibly in decisions.

It is intended for agencies, researchers, system architects, policymakers, disaster and humanitarian organisations, First Nations communities and Indigenous knowledge holders, civil society, and assurance bodies. It supports adoption at scale while protecting scientific credibility and public trust. It also reinforces Indigenous data sovereignty and appropriate use of Traditional Ecological Knowledge.

By strengthening credible and inclusive climate intelligence, P7021 supports resilience and adaptation priorities and contributes to the UN Sustainable Development Goals through responsible, equitable, and accountable climate AI deployment.`
    },
    {
      id: 'why',
      title: 'Why is it important?',
      icon: Target,
      color: 'bg-green-600',
      content: `Climate and disaster decisions increasingly rely on modelling outputs that shape policy, infrastructure investment, emergency planning, and community safety, and AI works as a double-edged sword that can create value with proper governance but can be harmful without it. Therefore, it is important to support the responsible adoption of AI in climate projections in line with AI ethics principles.

A standard like IEEE P7021 matters because it translates ethical AI into practical steps that protect trust and legitimacy:

• It helps agencies and researchers produce projections that are transparent and explainable, so they can be tested, challenged, and improved.
• It helps policymakers and regulators make defensible decisions with clear accountability and human oversight, rather than treating AI outputs as unquestionable.
• It helps Digital Twin and systems teams build governance into design, not as an afterthought.
• It supports data sovereignty and the respectful use of knowledge, thereby strengthening participation and fairness.
• It helps disaster and humanitarian organisations reduce bias and improve equity in risk and warning systems.

Recent research (Randeniya, J.N., Haigh, R., & Amaratunga, D., 2025, "Responsible intelligence: ethical AI governance for climate prediction in the Australian context," AI and Ethics) reveals a concerning gap in AI adoption across climate institutions. Government agencies demonstrate AI adoption rates of only 2.1 out of 5, compared to academic institutions at 4.0 out of 5. Yet government agencies bear primary responsibility for disaster management and climate policy decisions. This paradox creates potential for systematic bias and reduced accountability in the systems that most directly affect public safety and infrastructure investment.

Overall, it supports resilience and sustainability and contributes to the United Nations Sustainable Development Goals (UN SDGs).`
    },
    {
      id: 'example',
      title: 'Real-world example',
      icon: CloudRain,
      color: 'bg-cyan-600',
      content: `One practical way this could help is through a multi-hazard climate intelligence and decision support service operated by meteorological agencies, disaster management authorities, infrastructure operators, local governments, and research partners.

It is designed to manage compounding extremes such as:
• Extreme rainfall that drives river and flash flooding
• Coastal storm surge that coincides with high tides
• Cyclones and typhoons that bring damaging wind and rain
• Heatwaves that raise health risk and energy demand
• Drought and fire weather that disrupt landscapes, livelihoods, and supply chains

In this program, machine learning and physics-informed models combine satellite observations, ground sensors, forecasts, historical impact data, and local knowledge to produce probabilistic, location-specific outlooks and practical planning scenarios weeks to seasons ahead. These outputs feed shared operational tools used for evacuation planning, hospital surge preparation, prioritising critical assets, staging relief logistics, and guiding recovery investment.

IEEE P7021 would help by making responsible AI a consistent practice, ensuring the service remains trustworthy when decisions are urgent. It would require transparent handling of uncertainty, stronger testing under changing conditions, equity checks to ensure risk is not missed in lower-capacity areas, and clear accountability so model updates and warning decisions are reviewed and authorised by accountable agents.

The result is a more sustainable and equitable climate service that supports safer action across many hazards and helps communities and decision-makers understand both what the system predicts and its limits.`
    },
    {
      id: 'stage',
      title: 'What stage is it at?',
      icon: Calendar,
      color: 'bg-amber-600',
      content: `The P7021 Project Authorization Request (PAR) was approved on 10 December 2025, and the standard is now in active development.

Timeline:
• Working group formation: January 2026 (current phase)
• Draft submission target: November 2027
• Projected completion: June 2028
• PAR expiration: December 2029

The working group is in its formation phase, establishing conceptual frameworks and identifying key stakeholders. Initial focus includes codifying principles that align AI capabilities with ethical requirements, addressing technical, regulatory, and social challenges, and designing interoperability between existing climate science practices and emerging AI governance frameworks.`
    },
    {
      id: 'who',
      title: 'Who should be involved?',
      icon: UserCheck,
      color: 'bg-purple-600',
      content: `P7021 will be most relevant to people who work at the intersection of climate risk, long-term projections, and the responsible use of AI. It will suit those who can contribute practical expertise in governance, leadership, regulation, and ethical principles.

This includes:
• Leaders and specialists from meteorological and climate agencies, national climate services, climate research institutions, and Earth observation teams who develop and operate modelling and projection programs
• AI engineers, data scientists, system architects, and product owners who design and deploy climate AI systems, including climate Digital Twins and decision-support platforms
• Disaster risk and early warning practitioners in emergency management agencies and humanitarian organisations who rely on climate risk outputs for preparedness, anticipatory action, and recovery
• First Nations and Indigenous representatives and knowledge holders to support Indigenous data sovereignty, culturally safe governance, and appropriate engagement with Traditional Ecological Knowledge
• Assurance, audit, and ethics professionals, along with legal and regulatory experts, who help set expectations for accountability, transparency, oversight, and compliance
• Senior policy and governance decision owners where AI-enabled projections directly shape public plans and investment decisions, to help ensure the standard supports defensible decision-making and responsible leadership in practice`
    },
    {
      id: 'frameworks',
      title: 'Related frameworks and standards',
      icon: Link,
      color: 'bg-rose-600',
      content: `P7021 aligns with UNESCO's AI Ethics Framework and is designed to translate globally recognised ethical principles into climate-ready, implementable practice for long-term projections and DRR.

It is informed by established standards and initiatives such as:
• IEEE P7000 series
• ISO approaches to resilience and continuity
• United Nations Office for Disaster Risk Reduction (UNDRR) Sendai Framework

P7021 addresses a clear gap—there is currently no dedicated standard focused on ethical AI implementation for long-term climate projections and DRR.

By strengthening trustworthy, inclusive climate intelligence, P7021 supports UN Sustainable Development Goals 1, 3, 9, 10, 11, 13, 16, and 17:
• SDG 1: No Poverty
• SDG 3: Good Health and Well-being
• SDG 9: Industry, Innovation and Infrastructure
• SDG 10: Reduced Inequalities
• SDG 11: Sustainable Cities and Communities
• SDG 13: Climate Action
• SDG 16: Peace, Justice and Strong Institutions
• SDG 17: Partnerships for the Goals

In this way, P7021 supports global, national, and peak-body agendas by enabling adoption at scale across government, enterprise, not-for-profit, and profit-for-purpose sectors, reinforcing resilience, equity, transparency, and accountable governance for AI-enabled climate decision infrastructure.`
    }
  ];

  const kiteDimensions = [
    {
      id: 'ai',
      title: 'AI',
      color: 'bg-blue-600',
      icon: Cpu,
      summary: 'Technical and operational characteristics of AI systems',
      description: 'This dimension focuses on the technical and operational characteristics of AI systems, including data, models, algorithms, and deployment contexts. It prompts questions about capability, limitations, risk, and oversight across the AI lifecycle.'
    },
    {
      id: 'organisation',
      title: 'Organisation',
      color: 'bg-green-600',
      icon: Building,
      summary: 'Strategy, governance structures, and accountability',
      description: 'This dimension examines strategy, governance structures, accountability, leadership, and culture. It highlights how organisational decision-making, incentives, and risk management practices shape responsible AI outcomes.'
    },
    {
      id: 'society',
      title: 'Society',
      color: 'bg-purple-600',
      icon: Users,
      summary: 'Social justice, diversity, equity, and inclusion',
      description: 'This dimension addresses social justice, diversity, equity, inclusion, and community impact. It ensures that AI initiatives consider affected stakeholders, mitigate bias, and respect human rights and societal values.'
    },
    {
      id: 'sustainability',
      title: 'Sustainability',
      color: 'bg-amber-600',
      icon: Leaf,
      summary: 'Long-term environmental, social, and economic impact',
      description: 'This dimension evaluates long-term environmental, social, and economic impact, including alignment with sustainability objectives such as the UN Sustainable Development Goals. It shifts focus from short-term optimisation to durable, future-oriented value.'
    }
  ];

  const windTurbineComponents = [
    {
      id: 'wind',
      title: 'Wind',
      subtitle: 'Sustainability Requirements and Risks',
      color: 'bg-cyan-500',
      icon: Wind,
      description: 'The "wind" represents the external forces acting on AI systems, including sustainability imperatives, societal expectations, regulatory pressures, and emerging risks. These forces are not optional; they are ever-present and continuously changing. Effective AI governance does not resist the wind—it is designed to absorb and respond to it responsibly.'
    },
    {
      id: 'blades',
      title: 'Blades',
      subtitle: 'Organisational Values and Ethics',
      color: 'bg-slate-600',
      icon: Settings,
      description: 'The turbine blades symbolise an organisation\'s values, ethical principles, and cultural commitments. These blades determine how sustainability demands and risks are captured. If values are poorly defined or misaligned, the system becomes inefficient or unstable. Strong ethical foundations ensure that AI initiatives absorb external pressures in a way that is fair, inclusive, and aligned with purpose.'
    },
    {
      id: 'gearbox',
      title: 'Gearbox',
      subtitle: 'Partnerships, Volunteering, and Collaboration',
      color: 'bg-gray-700',
      icon: Users,
      description: 'The gearbox represents the mechanisms that translate values into coordinated action. This includes partnerships, cross-sector collaboration, community engagement, and shared learning. Just as a gearbox synchronises motion, collaboration aligns diverse stakeholders—ensuring AI governance is not siloed, but collectively reinforced.'
    },
    {
      id: 'generator',
      title: 'Generator',
      subtitle: 'AI Capability and Implementation',
      color: 'bg-blue-700',
      icon: Cpu,
      description: 'The generator symbolises the organisation\'s AI capabilities: data, models, systems, and technical expertise. Governance does not replace innovation; it enables AI capability to operate safely, responsibly, and effectively, converting ethical alignment into operational performance.'
    },
    {
      id: 'power',
      title: 'Power Output',
      subtitle: 'Ethical Impact and Social Value',
      color: 'bg-yellow-500',
      icon: Zap,
      description: 'The electricity generated represents the productive outcome of AI: ethical impact, social benefit, trust, and long-term value creation. This is where AI delivers tangible benefits—improving services, supporting communities, enhancing decision-making, and contributing to sustainable development.'
    },
    {
      id: 'yaw',
      title: 'Yaw System',
      subtitle: 'Leadership and Strategic Guidance',
      color: 'bg-rose-600',
      icon: Compass,
      description: 'Leadership functions as the directional control system, ensuring the turbine consistently faces the right direction. Governance frameworks, strategic oversight, and ethical leadership guide AI initiatives so they remain aligned with organisational purpose and societal good, even as conditions change.'
    }
  ];

  const complexityExamples = [
    {
      title: 'Environmental & Social',
      description: 'AI has been identified as a key enabler for approximately 79% of UN SDG targets, yet around 35% of targets may be negatively impacted if AI is poorly governed.'
    },
    {
      title: 'Environmental & Technological',
      description: 'Cloud computing enables scalability, but growing data transfer raises concerns about energy consumption and carbon footprint.'
    },
    {
      title: 'Economic & Sustainability',
      description: 'AI is projected to contribute trillions to the global economy, while SDGs target transformative outcomes for people and planet.'
    },
    {
      title: 'Economic & Social',
      description: 'A high proportion of AI initiatives fail due to bias in data, algorithms, or organisational processes.'
    },
    {
      title: 'Economic & Ethical',
      description: 'Comprehensive AI legislation is still emerging, introducing risks such as automation-driven inequality and algorithmic bias.'
    }
  ];

  const keyFactors = [
    'Diversity, equity, and inclusion in AI design and deployment',
    'Alignment with sustainability and development goals',
    'Clear organisational accountability and governance',
    'Ethical oversight across data, algorithms, and teams',
    'Risk mitigation for bias, misuse, and unintended harm'
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight mb-8 animate-in fade-in slide-in-from-bottom-8 duration-1000">
              Resources &
              <span className="block font-normal mt-2">Publications</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Frameworks, research, and thought leadership for safe, responsible, and sustainable AI
            </p>
          </div>
        </div>
      </section>

      {/* Frameworks Overview */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <h2 className="text-3xl font-light mb-8 text-center">Our Innovative Frameworks & Contributions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="hover:shadow-xl transition-all duration-300 overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-blue-600 via-green-600 to-amber-600"></div>
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="inline-block px-3 py-1 bg-black text-white text-xs font-medium rounded-full">
                    Featured
                  </div>
                  <span className="text-xs text-gray-500">UN & IFRC Adopted</span>
                </div>
                <h3 className="text-2xl font-light mb-3">KITE Framework</h3>
                <p className="text-gray-600 mb-4">An abstraction framework for reducing complexity in AI governance through four interdependent dimensions.</p>
                <a href="#kite-framework" className="text-black font-medium hover:underline">Learn more →</a>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-all duration-300 overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-cyan-500 via-yellow-500 to-rose-600"></div>
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="inline-block px-3 py-1 bg-black text-white text-xs font-medium rounded-full">
                    Featured
                  </div>
                  <span className="text-xs text-gray-500">IFRC Endorsed</span>
                </div>
                <h3 className="text-2xl font-light mb-3">Wind Turbine Model</h3>
                <p className="text-gray-600 mb-4">A dynamic model for understanding how organisations convert ethical intent into real, positive AI impact.</p>
                <a href="#wind-turbine-model" className="text-black font-medium hover:underline">Learn more →</a>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-all duration-300 overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-blue-600 via-green-500 to-amber-500"></div>
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="inline-block px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded-full">
                    Active Contributor
                  </div>
                  <span className="text-xs text-gray-500">IEEE Standard</span>
                </div>
                <h3 className="text-2xl font-light mb-3">IEEE P7021</h3>
                <p className="text-gray-600 mb-4">Recommended practice for implementing ethical AI in long-term climate projections towards sustainability.</p>
                <a href="#ieee-p7021" className="text-black font-medium hover:underline">Learn more →</a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ============================================= */}
      {/* KITE FRAMEWORK SECTION */}
      {/* ============================================= */}
      
      <div id="kite-framework">
        {/* KITE Framework Introduction */}
        <section className="py-24 bg-white">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-block px-4 py-2 bg-black text-white text-sm font-medium rounded-full mb-6">
                  Featured Framework
                </div>
                <h2 className="text-4xl sm:text-5xl font-light tracking-tight mb-6">
                  KITE: An Abstraction Framework for Reducing Complexity in AI Governance
                </h2>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  An innovative framework developed by the Centre for Sustainable AI, adapted by the <strong>United Nations</strong>, the <strong>International Federation of Red Cross and Red Crescent Societies</strong>, and many other organisations supporting the UN Sustainable Development Goals and beyond.
                </p>
                <div className="flex flex-wrap gap-3">
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">UN Adopted</span>
                  <span className="px-3 py-1 bg-red-100 text-red-800 text-sm rounded-full">IFRC Endorsed</span>
                  <span className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">SDG Aligned</span>
                </div>
              </div>
              <div className="flex justify-center">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_forai-portfolio/artifacts/yn5jxonp_KITE_High_Resolution_Trans.png" 
                    alt="KITE Framework Diagram" 
                    className="w-full max-w-md"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* KITE Quote Section */}
        <section className="py-16 bg-black text-white">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-2xl sm:text-3xl font-light leading-relaxed italic">
                "Everything invented in the past 150 years will be reinvented using AI within the next 15 years."
              </p>
              <p className="text-gray-400 mt-6">
                Unlike any other technology, artificial intelligence brings unprecedented decision-making power, making ethics, governance, and accountability central concerns rather than afterthoughts.
              </p>
            </div>
          </div>
        </section>

        {/* KITE Introduction Content */}
        <section className="py-24 bg-white">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-light mb-8">Introduction</h2>
              <div className="space-y-6 text-gray-700 leading-relaxed">
                <p>
                  As AI systems increasingly influence economic activity, public services, environmental outcomes, and social structures, organisations face growing complexity. Diversity, equity, and inclusion (DEI) are now recognised as essential for mitigating AI risks and advancing social justice. At the same time, sustainability has emerged as a multi-dimensional challenge that spans environmental, social, and economic domains.
                </p>
                <p>
                  Governments, businesses, not-for-profits, and civil society organisations are pursuing AI strategies for efficiency, innovation, and impact—often simultaneously. Partnerships and collaboration are more important than ever, as distributed and diverse data sources power AI, while bias remains one of its most significant risks.
                </p>
                <p className="font-medium">
                  In this context, the need for a clear abstraction framework to simplify and align AI governance has become increasingly evident.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* KITE Complexity Section */}
        <section className="py-24 bg-gray-50">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
            <h2 className="text-3xl sm:text-4xl font-light mb-6">Complexity in AI Governance</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-12 max-w-4xl">
              The AI landscape spans a wide spectrum—from IoT sensor networks and data analytics to autonomous systems and smart cities. Each application introduces multiple governance perspectives, including technology, strategy, ethics, sustainability, regulation, compliance, and social impact.
            </p>

            <h3 className="text-xl font-medium mb-6">Examples of Interconnected Complexity</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {complexityExamples.map((example, index) => (
                <Card key={index} className="hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <h4 className="text-lg font-medium mb-3">{example.title}</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">{example.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="mt-12 p-6 bg-white rounded-xl border-l-4 border-black">
              <p className="text-gray-700 leading-relaxed">
                Despite this diversity, many AI initiatives share common human, cultural, and strategic foundations. Organisations pursuing different objectives—economic, social, or environmental—often face similar governance challenges. These shared foundations create opportunities for collaboration, provided complexity can be meaningfully reduced.
              </p>
            </div>
          </div>
        </section>

        {/* KITE Four Dimensions */}
        <section className="py-24 bg-white">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-light mb-4">The KITE Abstraction Framework</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                KITE provides a structured way to simplify AI governance without oversimplifying its impact. The framework is organised around four interdependent dimensions that apply across sectors and use cases.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {kiteDimensions.map((dimension) => {
                const IconComponent = dimension.icon;
                const isExpanded = expandedSection === dimension.id;
                
                return (
                  <Card 
                    key={dimension.id}
                    className={`cursor-pointer transition-all duration-300 ${isExpanded ? 'ring-2 ring-black shadow-xl' : 'hover:shadow-lg'}`}
                    onClick={() => toggleSection(dimension.id)}
                  >
                    <CardContent className="p-0">
                      <div className={`${dimension.color} text-white p-6`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                              <IconComponent className="w-6 h-6" />
                            </div>
                            <div>
                              <h3 className="text-2xl font-light">{dimension.title}</h3>
                              <p className="text-white/80 text-sm mt-1">{dimension.summary}</p>
                            </div>
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-6 h-6" />
                          ) : (
                            <ChevronDown className="w-6 h-6" />
                          )}
                        </div>
                      </div>
                      
                      {isExpanded && (
                        <div className="p-6 bg-gray-50 animate-in fade-in slide-in-from-top-2 duration-300">
                          <p className="text-gray-700 leading-relaxed">{dimension.description}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* KITE People & Culture Section */}
        <section className="py-24 bg-gradient-to-b from-gray-900 to-black text-white">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-pink-500 rounded-full flex items-center justify-center">
                    <Heart className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-light">
                    AI for Good at the Centre: People and Culture
                  </h2>
                </div>
                <p className="text-lg text-gray-300 leading-relaxed mb-6">
                  At the centre of the KITE framework lies <strong className="text-white">People and Culture</strong>, reinforcing that AI governance is ultimately a human challenge. Technical controls alone cannot ensure ethical or sustainable AI.
                </p>
                <p className="text-gray-400 leading-relaxed">
                  Governance succeeds when organisations embed responsible AI into culture, capability, and everyday decision-making. By positioning AI for Good as a vertical axis and People and Culture as the horizontal anchor, the framework highlights the need to balance innovation with responsibility, and performance with trust.
                </p>
              </div>
              <div className="flex justify-center">
                <div className="bg-white rounded-2xl p-8">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_forai-portfolio/artifacts/yn5jxonp_KITE_High_Resolution_Trans.png" 
                    alt="KITE Framework - People & Culture at Centre" 
                    className="w-full max-w-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* KITE Why It Matters */}
        <section className="py-24 bg-white">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-black rounded-full flex items-center justify-center">
                    <Target className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-light">Why KITE Matters</h2>
                </div>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The strength of the KITE framework lies in its ability to hide application-specific complexity while surfacing key success factors that are broadly applicable.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  By abstracting AI governance into shared dimensions, KITE enables collaboration across sectors, supports systematic stakeholder engagement, and helps organisations clearly understand their responsibilities in advancing ethical and sustainable AI.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-medium mb-6">Key Success Factors</h3>
                <div className="space-y-4">
                  {keyFactors.map((factor, index) => (
                    <div key={index} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="w-6 h-6 bg-black rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <p className="text-gray-700">{factor}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* KITE Conclusion */}
        <section className="py-24 bg-gray-50">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-light mb-8">Conclusion</h2>
              <div className="space-y-6 text-gray-700 leading-relaxed">
                <p>
                  As AI continues to reshape societies, economies, and environments, governance frameworks must evolve to manage complexity without losing clarity. The KITE abstraction framework offers a practical and human-centred way to align AI innovation with ethics, sustainability, and social justice.
                </p>
                <p className="text-xl font-medium text-black">
                  By focusing on AI, organisation, society, and sustainability—anchored by people and culture—KITE provides a common language for leaders, policymakers, technologists, and communities working toward a safe, responsible, and sustainable AI future.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* ============================================= */}
      {/* WIND TURBINE MODEL SECTION */}
      {/* ============================================= */}

      <div id="wind-turbine-model">
        {/* Wind Turbine Introduction */}
        <section className="py-24 bg-black text-white">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-block px-4 py-2 bg-white text-black text-sm font-medium rounded-full mb-6">
                  Conceptual Model
                </div>
                <h2 className="text-4xl sm:text-5xl font-light tracking-tight mb-6">
                  Wind Turbine Model for AI Ethics and Governance
                </h2>
                <p className="text-lg text-gray-300 leading-relaxed mb-6">
                  A practical and intuitive way to understand how organisations can convert ethical intent and sustainability requirements into real, positive AI impact.
                </p>
                <p className="text-gray-400 leading-relaxed mb-6">
                  Inspired by the mechanics of a wind turbine, the model frames AI governance as a dynamic system—one that continuously responds to external forces, internal values, and leadership direction.
                </p>
                <div className="flex flex-wrap gap-3">
                  <span className="px-3 py-1 bg-red-100 text-red-800 text-sm rounded-full">IFRC Endorsed</span>
                </div>
              </div>
              <div className="flex justify-center">
                <div className="bg-white rounded-2xl p-8">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_forai-portfolio/artifacts/1s1viqb0_AI_Framework_Windmill.drawio.png" 
                    alt="Wind Turbine Model for AI Ethics and Governance" 
                    className="w-full max-w-lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Wind Turbine Overview */}
        <section className="py-24 bg-white">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-light mb-8">Overview</h2>
              <div className="space-y-6 text-gray-700 leading-relaxed">
                <p>
                  The Wind Turbine Conceptual Model for AI Ethics and Governance provides a practical and intuitive way to understand how organisations can convert ethical intent and sustainability requirements into real, positive AI impact. Inspired by the mechanics of a wind turbine, the model frames AI governance as a dynamic system—one that continuously responds to external forces, internal values, and leadership direction.
                </p>
                <p className="font-medium text-black">
                  Rather than treating AI ethics as a static policy exercise, this model emphasises motion, alignment, and transformation: how risks, values, and societal expectations are harnessed and transformed into productive, trustworthy outcomes.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Wind Turbine Components */}
        <section className="py-24 bg-gray-50">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-light mb-4">Key Components of the Model</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Each component of the wind turbine represents a critical element of AI governance, working together as a dynamic system.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {windTurbineComponents.map((component) => {
                const IconComponent = component.icon;
                const isExpanded = expandedWindSection === component.id;
                
                return (
                  <Card 
                    key={component.id}
                    className={`cursor-pointer transition-all duration-300 ${isExpanded ? 'ring-2 ring-black shadow-xl md:col-span-2 lg:col-span-3' : 'hover:shadow-lg'}`}
                    onClick={() => toggleWindSection(component.id)}
                  >
                    <CardContent className="p-0">
                      <div className={`${component.color} text-white p-6`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                              <IconComponent className="w-6 h-6" />
                            </div>
                            <div>
                              <h3 className="text-xl font-light">{component.title}</h3>
                              <p className="text-white/80 text-sm mt-1">{component.subtitle}</p>
                            </div>
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-6 h-6" />
                          ) : (
                            <ChevronDown className="w-6 h-6" />
                          )}
                        </div>
                      </div>
                      
                      {isExpanded && (
                        <div className="p-6 bg-white animate-in fade-in slide-in-from-top-2 duration-300">
                          <p className="text-gray-700 leading-relaxed">{component.description}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Wind Turbine Visual */}
        <section className="py-24 bg-white">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
            <div className="flex justify-center mb-12">
              <div className="bg-white rounded-2xl p-8 max-w-3xl shadow-lg">
                <img 
                  src="https://customer-assets.emergentagent.com/job_forai-portfolio/artifacts/1s1viqb0_AI_Framework_Windmill.drawio.png" 
                  alt="Wind Turbine Model - Complete View" 
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Why This Model Matters */}
        <section className="py-24 bg-gray-50">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-14 h-14 bg-black rounded-full flex items-center justify-center">
                  <Lightbulb className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-3xl sm:text-4xl font-light">Why This Model Matters</h2>
              </div>
              <div className="space-y-6 text-gray-700 leading-relaxed">
                <p>
                  This wind turbine model demonstrates that responsible AI is not achieved through isolated controls, but through a coordinated system of values, leadership, partnerships, and capability. It is adaptable across industries, sectors, and organisational sizes, making it a powerful abstraction for AI ethics and governance.
                </p>
                <p className="text-xl font-medium text-black">
                  By visualising AI governance as an energy system—one that transforms complexity into positive impact—the model helps organisations understand how to move from intention to action, and from compliance to trust.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* ============================================= */}
      {/* IEEE P7021 STANDARDS CONTRIBUTION SECTION */}
      {/* ============================================= */}

      <div id="ieee-p7021">
        {/* P7021 Introduction */}
        <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <div className="inline-block px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-full mb-6">
                Active Contributor
              </div>
              <h2 className="text-4xl sm:text-5xl font-light tracking-tight mb-6">
                IEEE P7021 PAR
              </h2>
              <p className="text-xl text-gray-700 leading-relaxed mb-4">
                Recommended Practice for Implementing Ethical Artificial Intelligence (AI) in Long-Term Climate Projections Towards Sustainability
              </p>
              <div className="flex flex-wrap justify-center gap-3 mt-6">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">IEEE Standard</span>
                <span className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">Climate AI</span>
                <span className="px-3 py-1 bg-amber-100 text-amber-800 text-sm rounded-full">In Development</span>
              </div>
            </div>

            {/* P7021 Key Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
              <Card className="text-center">
                <CardContent className="p-6">
                  <p className="text-3xl font-light text-blue-600 mb-2">Dec 2025</p>
                  <p className="text-sm text-gray-600">PAR Approved</p>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="p-6">
                  <p className="text-3xl font-light text-green-600 mb-2">Nov 2027</p>
                  <p className="text-sm text-gray-600">Draft Target</p>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="p-6">
                  <p className="text-3xl font-light text-amber-600 mb-2">Jun 2028</p>
                  <p className="text-sm text-gray-600">Completion</p>
                </CardContent>
              </Card>
              <Card className="text-center group relative">
                <CardContent className="p-6">
                  <p className="text-3xl font-light text-purple-600 mb-2">8</p>
                  <p className="text-sm text-gray-600">UN SDGs Supported</p>
                  <p className="text-xs text-gray-400 mt-1">SDGs 1, 3, 9, 10, 11, 13, 16, 17</p>
                </CardContent>
              </Card>
            </div>

            {/* P7021 Expandable Sections */}
            <div className="space-y-4">
              {p7021Sections.map((section) => {
                const IconComponent = section.icon;
                const isExpanded = expandedP7021Section === section.id;
                
                return (
                  <Card 
                    key={section.id}
                    className={`cursor-pointer transition-all duration-300 ${isExpanded ? 'ring-2 ring-black shadow-xl' : 'hover:shadow-lg'}`}
                    onClick={() => toggleP7021Section(section.id)}
                  >
                    <CardContent className="p-0">
                      <div className={`${section.color} text-white p-6`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                              <IconComponent className="w-6 h-6" />
                            </div>
                            <h3 className="text-xl font-light">{section.title}</h3>
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-6 h-6" />
                          ) : (
                            <ChevronDown className="w-6 h-6" />
                          )}
                        </div>
                      </div>
                      
                      {isExpanded && (
                        <div className="p-6 bg-gray-50 animate-in fade-in slide-in-from-top-2 duration-300">
                          <div className="prose prose-gray max-w-none">
                            {section.content.split('\n\n').map((paragraph, idx) => (
                              <p key={idx} className="text-gray-700 leading-relaxed mb-4 last:mb-0 whitespace-pre-line">
                                {paragraph}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      </div>

      {/* ============================================= */}
      {/* GLOBAL ADOPTION SECTION */}
      {/* ============================================= */}

      {/* Global Adoption */}
      <section className="py-24 bg-black text-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center">
                <Globe className="w-7 h-7 text-black" />
              </div>
            </div>
            <h2 className="text-3xl sm:text-4xl font-light mb-4">Global Adoption</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Our frameworks have been adopted by leading international organisations committed to responsible AI governance
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="text-xl font-light mb-2">United Nations</h3>
                <p className="text-gray-400 text-sm">Supporting SDG implementation through responsible AI governance</p>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-red-400" />
                </div>
                <h3 className="text-xl font-light mb-2">IFRC</h3>
                <p className="text-gray-400 text-sm">International Federation of Red Cross and Red Crescent Societies</p>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Leaf className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-xl font-light mb-2">SDG Partners</h3>
                <p className="text-gray-400 text-sm">Organisations worldwide aligned with sustainable development goals</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resources;
